// Remembers the page a user was on before getting bounced to /auth (or
// before explicitly choosing to sign in from a public page), so a
// successful sign-in can return them there instead of always landing on
// "/". Session-scoped (not localStorage): a stale return-to path shouldn't
// survive across browser sessions and silently redirect a later, unrelated
// login.
//
// Google sign-in is a full-page OAuth round-trip (Amplify's
// signInWithRedirect) — router state doesn't survive that, so this can't
// live in React Router's location.state. sessionStorage does.

const KEY = 'qlearn:return-to'

/**
 * Validates `path` as a safe, internal, same-origin destination and returns
 * its canonical form (pathname + search) — or null when it isn't safe.
 *
 * A previous version of this check used string prefixes only (must start
 * with "/", must not start with "//", must not start with "/auth"). That's
 * bypassable: the WHATWG URL parser normalizes characters those checks
 * don't account for before resolving a relative reference against a base.
 * For special schemes (http/https) it treats "\" as equivalent to "/", and
 * it strips ASCII tabs/newlines from the input outright — both before the
 * path/authority split is even decided. So e.g. "/\\evil.com" and
 * "/\t/evil.com" both *string*-check as "starts with one /, not //", yet
 * both parse to "//evil.com" → resolve to a different origin entirely. Once
 * such a value is handed to react-router's navigate(), a pushState that
 * throws SecurityError (cross-origin) falls back to a real
 * location.assign() — a genuine open redirect.
 *
 * Rather than adding more string checks to out-guess the next normalization
 * quirk, we let the same URL parser the browser uses make the call: resolve
 * `path` against our own origin and require the result to still BE our own
 * origin. Returning the parsed pathname + search (not the raw input) also
 * means whatever survives validation is already canonical, so a downstream
 * consumer can't re-parse the raw string and land on a different answer
 * than we just did.
 */
function sanitizeReturnTo(path: string): string | null {
  if (!path.startsWith('/')) return null
  // Looping back to the auth screen itself is pointless. Checked on the raw
  // input, same as before — this is a UX guard, not the security boundary
  // (that's the origin check below).
  if (path.startsWith('/auth')) return null
  try {
    const url = new URL(path, window.location.origin)
    if (url.origin !== window.location.origin) return null
    return url.pathname + url.search
  } catch {
    return null
  }
}

/**
 * Remember `path` as where to return after a successful sign-in. Silently
 * ignores unsafe paths (see sanitizeReturnTo) and sessionStorage failures
 * (private browsing / quota) — this is a UX nicety, never worth failing the
 * surrounding flow over.
 */
export function rememberReturnTo(path: string): void {
  const safe = sanitizeReturnTo(path)
  if (!safe) return
  try {
    sessionStorage.setItem(KEY, safe)
  } catch {
    /* private mode / quota — silently drop */
  }
}

/**
 * Read and clear the remembered return-to path, re-validating it against
 * the same safety rules — defense against a tampered stored value, since we
 * never trust sessionStorage content blindly. Returns null when there's
 * nothing (safe) to return to.
 */
export function consumeReturnTo(): string | null {
  try {
    const raw = sessionStorage.getItem(KEY)
    sessionStorage.removeItem(KEY)
    return raw ? sanitizeReturnTo(raw) : null
  } catch {
    return null
  }
}
